import { FaGripVertical, FaBuilding } from "react-icons/fa";
import { TbExternalLink, TbMail, TbAlertTriangle, TbSearch } from "react-icons/tb";

import { FiDollarSign } from "react-icons/fi";
import { formatDate, formatMoney } from "utils/functions";
import { useNavigate } from "react-router-dom";
import { DraggableSyntheticListeners } from "@dnd-kit/core";
import { Checkbox, Progress, Space, Tooltip, Typography } from "antd";
import { ApplicationCardDto, CardDto, CreditorCardDto, LoanCardDto } from "dtos/kanban.dto";
import { useMemo } from "react";
import { ApplicationDto } from "dtos/application.dto";

export default function TaskCard({
  itemData,
  dragListeners,
  select,
}: Readonly<{
  itemData: CardDto;
  dragListeners?: DraggableSyntheticListeners;
  select?: {
    checked: boolean;
    onCheck: (check: boolean) => unknown;
    show: boolean;
  };
}>) {
  const navigate = useNavigate();

  const topLeftContent = useMemo(
    () =>
      select?.show === true ? (
        <Checkbox
          key="checkbox"
          className="mt-1"
          checked={select.checked}
          onChange={(e) => select.onCheck(e.target.checked)}
        />
      ) : (
        <FaGripVertical
          {...dragListeners}
          key="drag-and-drop"
          className="size-4 min-w-4 text-gray-300 group-hover:text-gray-400 mt-1 cursor-grab"
        />
      ),
    [select],
  );

  if (itemData?.type.toLocaleLowerCase() === "application") {
    const app = itemData?.item as ApplicationCardDto;

    return (
      <div className="w-full shadow bg-white rounded-xl border border-solid border-gray-200 overflow-hidden group">
        {/* HEADER */}
        <div className="flex items-start justify-between !px-4 pt-3">
          <div className="flex items-start gap-3">
            {topLeftContent}

            <Space.Compact direction="vertical" className="!gap-0 max-w-[180px]">
              <Typography.Text
                ellipsis
                italic={!app?.meta?.client_name?.length}
                className="text-sm font-semibold truncate w-[160px] !mb-0 !leading-0"
              >
                {app?.meta?.client_name || "Not Available"}
              </Typography.Text>

              <Space className="items-center gap-1">
                <TbMail size={12} className="text-gray-400 flex-1 grow min-w-3" />
                <Typography.Text
                  ellipsis
                  italic={!app?.meta?.email?.length}
                  className="text-xs max-w-[180px] text-gray-500 truncate mb-0.5"
                >
                  {app?.meta?.email || "Not Available"}
                </Typography.Text>
              </Space>
            </Space.Compact>
          </div>

          <TbExternalLink
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/dashboard/application/${app?.app_key}`);
            }}
            className="cursor-pointer hover:text-purple-600 size-4 min-w-4"
          />
        </div>

        {/* BODY */}
        <div className="px-4 py-2 border-t border-gray-100">
          <div className="flex items-center gap-2 mb-3">
            <FaBuilding className="text-purple-500" />
            <span className="text-sm truncate">{app?.organisation?.meta?.company_name ?? "Not Available"}</span>
          </div>

          <div className="flex items-center gap-2">
            <FiDollarSign className="text-yellow-500" />
            <span className="font-semibold">{formatMoney(app?.enrolled_debt ?? 0)}</span>
          </div>
        </div>

        {/* FOOTER */}
        <div className="px-4 py-3 border-t bg-purple-50">
          <div className="flex justify-between">
            <div>
              <p className="text-xs text-gray-500">Affiliate</p>
              <p className="text-xs font-medium">{app?.affiliate?.name ?? "No Affiliate"}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Created At</p>
              <p className="text-xs font-medium">{formatDate(app?.createdAt ?? null)}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (itemData?.type.toLocaleLowerCase() === "creditor") {
    const creditor = itemData?.item as CreditorCardDto;

    return (
      <div className="w-full bg-white rounded-xl shadow border border-solid border-gray-200 overflow-hidden group">
        <div className="flex items-start justify-between px-4 pt-3">
          <div className="flex items-start gap-3">
            {topLeftContent}

            <div className="flex-1 min-w-0">
              <Typography.Text ellipsis className="text-sm font-semibold truncate w-[160px] block">
                {creditor?.name ?? "Not Available"}
              </Typography.Text>

              <div className="flex items-start gap-1">
                <TbMail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <Typography.Paragraph ellipsis className="!m-0 text-xs text-gray-500">
                  {creditor?.allowed_emails?.[0] ?? "Not Available"}
                </Typography.Paragraph>
              </div>
            </div>
          </div>

          <TbSearch
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/dashboard/creditors?search=${creditor.name}`);
            }}
            className="cursor-pointer hover:text-emerald-600 size-4 min-w-4"
          />
        </div>

        <div className="px-4 py-2 border-t border-gray-100 space-y-2 text-xs text-gray-600">
          <div className="flex items-center justify-between">
            <span>Alternate Names</span>
            <Tooltip title={creditor?.alternate_names?.length ? creditor?.alternate_names?.join(", ") : "None"}>
              <span className="font-medium cursor-help">{creditor?.alternate_names?.length ?? 0}</span>
            </Tooltip>
          </div>

          <div className="flex items-center justify-between">
            <span>Allowed Emails</span>
            <Tooltip title={creditor?.allowed_emails?.length ? creditor?.allowed_emails?.join(", ") : "None"}>
              <span className="font-medium cursor-help">{creditor?.allowed_emails?.length ?? 0}</span>
            </Tooltip>
          </div>

          <div className="flex items-center justify-between">
            <span>Allowed Phones</span>
            <Tooltip title={creditor?.allowed_phones?.length ? creditor?.allowed_phones?.join(", ") : "None"}>
              <span className="font-medium cursor-help">{creditor?.allowed_phones?.length ?? 0}</span>
            </Tooltip>
          </div>

          <div className="flex items-center justify-between">
            <span>Allowed Domains</span>
            <Tooltip title={creditor?.allowed_domains?.length ? creditor?.allowed_domains?.join(", ") : "None"}>
              <span className="font-medium cursor-help">{creditor?.allowed_domains?.length ?? 0}</span>
            </Tooltip>
          </div>
        </div>

        <div className="px-4 py-3 border-t bg-purple-50">
          <div className="flex justify-between">
            <div>
              <p className="text-xs text-gray-500">Type</p>
              <p className="text-xs font-medium">Creditor</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Created At</p>
              <p className="text-xs font-medium">{formatDate(creditor?.createdAt ?? null)}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (itemData?.type.toLocaleLowerCase() === "loan") {
    const loan = itemData?.item as LoanCardDto & { application: ApplicationDto };
    // const priorityColor = PRIORITY_COLOR[loan?.priority] ?? "default";
    const frequencyLabel = loan?.frequency || "—";

    const paybackTotal = loan?.payback_amount ?? 0;
    const paidOff =
      paybackTotal > 0 ? Math.max(0, Math.min(100, ((paybackTotal - (loan.due_amount ?? 0)) / paybackTotal) * 100)) : 0;

    return (
      <div className="w-full bg-white rounded-xl shadow border border-solid border-gray-200 overflow-hidden group">
        {/* HEADER */}
        <div className="flex items-start justify-between px-4 pt-3">
          <div className="flex items-start gap-3">
            {topLeftContent}

            <div className="flex-1 min-w-0">
              <Typography.Text ellipsis className="text-sm font-semibold truncate w-[160px] block">
                {itemData.title ?? "Not Available"}
              </Typography.Text>

              <div className="flex items-center gap-1">
                <FiDollarSign className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                <Typography.Paragraph ellipsis className="!m-0 text-xs text-gray-500">
                  {formatMoney(loan?.funded_amount ?? 0).replace("$", "")} funded
                </Typography.Paragraph>
              </div>
            </div>
          </div>

          {loan?._id && (
            <TbExternalLink
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/dashboard/application/${loan?.application.app_key}`);
              }}
              className="cursor-pointer hover:text-indigo-600"
            />
          )}
        </div>

        {/* BODY */}
        <div className="px-4 py-2 border-t border-gray-100 space-y-2 text-xs text-gray-600">
          <div className="flex items-center justify-between">
            <span>Current Due</span>
            <span className="font-medium text-gray-800">{formatMoney(loan?.due_amount ?? 0)}</span>
          </div>

          <div className="flex items-center justify-between">
            <span>Payment</span>
            <span className="font-medium text-gray-800">
              {formatMoney(loan?.payments ?? 0)} {frequencyLabel}
            </span>
          </div>

          {loan?.payback_amount != null && loan.payback_amount > 0 && (
            <div className="pt-1">
              <div className="flex justify-between mb-1">
                <span className="text-gray-500">Payback Progress</span>
                <span className="font-medium text-gray-800">
                  {formatMoney(paybackTotal - (loan?.due_amount ?? 0))} / {formatMoney(paybackTotal)}
                </span>
              </div>
              <Progress percent={paidOff} showInfo={false} size="small" strokeColor="#a855f7" trailColor="#f0f0f0" />
            </div>
          )}

          {loan?.tenure !== undefined && (
            <div className="flex items-center justify-between">
              <span>Tenure</span>
              <span className="font-medium text-gray-800">
                {Math.ceil(loan.tenure)} {loan.tenure <= 1 ? "week" : "weeks"}
              </span>
            </div>
          )}

          {/* <div className="flex items-center justify-between">
            <span>Aggression</span>
            <Tooltip title={`Aggression level: ${loan?.aggression_level ?? 0}/10`}>
              <span className="font-medium cursor-help text-gray-800">{loan?.aggression_level ?? 0}/10</span>
            </Tooltip>
          </div> */}
        </div>

        {/* FOOTER */}
        {/* <div hidden className="px-4 py-3 border-t bg-purple-50">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs text-gray-500">Priority</p>
              <Tag color={priorityColor} className="!m-0 !mt-0.5 text-xs capitalize">
                {loan?.priority?.toLowerCase() ?? "—"}
              </Tag>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">Weightage</p>
              <p className="text-xs font-medium">{loan?.weightage ?? 0}</p>
            </div>
          </div>
        </div> */}
      </div>
    );
  }

  return (
    <div className="w-full bg-white rounded-xl border border-dashed border-red-200 overflow-hidden group">
      <div className="flex items-start gap-3 px-4 pt-3">
        <FaGripVertical
          {...dragListeners}
          className="w-4 h-4 text-gray-300 group-hover:text-gray-400 mt-1 cursor-grab"
        />
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <TbAlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <Typography.Text className="text-sm font-semibold text-red-600">Unprocessable data type</Typography.Text>
        </div>
      </div>

      <div className="px-4 py-3 border-t border-gray-100">
        <Typography.Paragraph className="!m-0 text-xs text-gray-500">
          This card could not be rendered because its type is not recognized.
        </Typography.Paragraph>
        {itemData?.type && (
          <p className="text-xs text-gray-400 mt-1">
            Received type: <span className="font-mono text-gray-600">{String(itemData.type)}</span>
          </p>
        )}
      </div>

      <div className="px-4 py-3 border-t bg-red-50">
        <p className="text-xs text-gray-500">Please contact support if this persists.</p>
      </div>
    </div>
  );
}
